/* eslint-disable react-hooks/rules-of-hooks */
import React, { useEffect, useMemo, useState } from 'react';
import { SCHOOL } from '../../apollo/queries/school';
import { ROSTERPLAYERS } from '../../apollo/queries/rosterPlayers';
import { useLazyQuery, useQuery } from '@apollo/client';
import TeamBanner from '../../components/teamBanner/teamBanner';
import Loader from '../../components/loader/loader';
import Error from '../../components/error/error';
import { getTeamConfig, getEnvConfig, getCoachData } from '../../config/config';
import styles from '../pages.module.scss';
import Tabs from '../../components/tabs/tabs';
import { Link } from 'react-router-dom';
import { useActiveFilters } from '../../context/active-filter-context';
import { IListSeasons } from '../../graphql-types/listSeasons';
import { prepareSeasonsData } from '../../components/utilities/utilities';
import useListSeasons from '../../customHooks/useListSeasons';
import StaffMember from '../../components/staff/staffMember';
import RosterMembers from './rosterMembers';
import { GetRosterMembers, Roster } from '../../graphql-types/roster';
import { GET_ROSTER_BY_ID } from '../../apollo/queries/rosterById';
import Dropdown from '../../components/dropdown/dropdown';
import { ROSTER_SORT_OPTIONS } from '../../constants/filters';
import { RosterListOptionsType, getSortedRosterMembers } from '../../utils/getSortedRoster';

const RosterPage: React.FC = () => {
  const { seasonsData, seasonsLoading, seasonsError } = useListSeasons();
  const [sortOrder, setSortOrder] = useState<RosterListOptionsType>(ROSTER_SORT_OPTIONS[0].value);
  const { state } = useActiveFilters();
  const thisTeam = getTeamConfig();
  const envConfig = getEnvConfig();

  let SCHOOL_VARS = {
    variables: {
      id: thisTeam.schoolId
    }
  };

  const {
    loading: schoolLoading,
    error: schoolError,
    data: schoolData
  } = useQuery(SCHOOL, SCHOOL_VARS);

  const {
    data: rosterData,
    loading: rosterLoading,
    error: rosterError
  } = useQuery<Roster>(GET_ROSTER_BY_ID, {
    variables: {
      rosterId: thisTeam.teamId
    }
  });

  const [getRosterMembers, { loading: rpLoading, error: rpError, data: rpData }] =
    useLazyQuery<GetRosterMembers>(ROSTERPLAYERS, {
      errorPolicy: 'all',
      variables: {
        rosterId: thisTeam.teamId && thisTeam.teamId !== null ? thisTeam.teamId.toLowerCase() : '',
        role: 'PLAYER',
        seasonId: state.season?.value
      }
    });

  const rosterMembers = useMemo(() => rpData?.getRosterMembers ?? [], [rpData?.getRosterMembers]);

  const listOfSeasons: IListSeasons[] = seasonsData?.listSeasons;

  const wtnTeamAverageSingle = rosterData?.rosterById.worldTennisTeamNumbers?.find(
    wtn => wtn.type === 'SINGLE'
  );

  useEffect(() => {
    if (listOfSeasons) {
      getRosterMembers();
    }
  }, [getRosterMembers, listOfSeasons]);

  const staffMembers: any = getCoachData();

  const staffData =
    staffMembers?.length > 0 ? (
      staffMembers.map((obj: any, index: number) => (
        <StaffMember key={`staff-${index}`} data={obj}></StaffMember>
      ))
    ) : (
      <></>
    );

  const sortedPlayers = useMemo(
    () => (rosterMembers ? getSortedRosterMembers(rosterMembers, sortOrder) : []),
    [rosterMembers, sortOrder]
  );

  const dropdownValue = ROSTER_SORT_OPTIONS.find(option => option.value === sortOrder);

  if (schoolLoading || rpLoading || seasonsLoading || rosterLoading) {
    return <Loader />;
  }

  if (schoolError || (rpError && !rpData) || seasonsError || rosterError) {
    return <Error />;
  }

  return (
    <>
      <TeamBanner
        teamData={schoolData.school}
        thisTeam={thisTeam}
        view="team"
        options={prepareSeasonsData(listOfSeasons)}
        wtnTeamAverage={wtnTeamAverageSingle?.average}
      />
      <Tabs>
        <ul>
          <li>
            <Link to={`${envConfig.root}/`}>Schedule</Link>
          </li>
          {thisTeam.playersModule === true ? (
            <li>
              <Link to={`${envConfig.root}/Roster`} className={styles.selected}>
                Roster
              </Link>
            </li>
          ) : null}
        </ul>
      </Tabs>

      <div className={styles.siteWidth}>
        <div className={styles.roster}>
          <div className={styles.rosterHeading}>
            <h2>Roster</h2>
            <div>
              <Dropdown
                options={ROSTER_SORT_OPTIONS}
                label="Sort"
                value={dropdownValue}
                onChange={e => setSortOrder(e?.value as RosterListOptionsType)}
              />
            </div>
          </div>
          {sortedPlayers?.length > 0 ? (
            <RosterMembers rosterMembers={sortedPlayers} />
          ) : (
            <p className={styles.emptyPlayers}>
              There are currently no players for the selected year.
            </p>
          )}
          {staffMembers.length > 0 && (
            <>
              <h2>Coaches</h2>

              <div className={styles.staffMembers}>{staffData}</div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default RosterPage;
